import type { TimelineSummary, GanttTask } from '../types/gantt';

export type OfferModerationStatus = 'active' | 'disabled';

export type Offer = {
	id: string;
	planId: string;
	gardenerId: string;
	gardenerName: string;
	bands: { min: number; mid: number; max: number };
	status: 'pending' | 'accepted' | 'rejected';
	createdAt: string;
	moderationStatus?: OfferModerationStatus;
	message?: string;
	timelineEstimate?: string;
	projectStartDate?: string; // ISO date string - when gardener can start
	validUntil?: string; // YYYY-MM-DD – offer valid until (seeker can accept until this date)
	timelineSummary?: TimelineSummary; // for seeker view
	ganttData?: GanttTask[]; // for detailed view
	selectedGardeners?: string[]; // Names of selected gardeners
	timelineData?: GanttTask[]; // Structured timeline data from offer
	/** From API when loading offers for seeker (for display when plan not in list yet) */
	planTitle?: string;
	planCity?: string;
	/** Offer-specific plan copy when gardener edited/regenerated during offer; use for display instead of plan */
	planSnapshot?: any;
};

function normalizeOffer(offer: Offer): Offer {
	return {
		...offer,
		moderationStatus: offer.moderationStatus ?? 'active',
		message: offer.message ?? '',
		timelineEstimate: offer.timelineEstimate ?? ''
	};
}

function loadOffers(): Offer[] {
	const stored = localStorage.getItem('offers');
	if (stored) {
		try {
			const parsed = JSON.parse(stored) as Offer[];
			return parsed.map(normalizeOffer);
		} catch {
			// Ignore and use defaults
		}
	}

	return [
		{ id: 'o1', planId: 'pl1', gardenerId: 'g1', gardenerName: 'GreenWorks', bands: { min: 1200, mid: 1500, max: 1800 }, status: 'pending', createdAt: new Date().toISOString(), moderationStatus: 'active', message: 'Includes prep, planting, and first maintenance visit.', timelineEstimate: 'Start within 2 weeks, completion in 4-5 weeks.' },
		{ id: 'o2', planId: 'pl1', gardenerId: 'g2', gardenerName: 'UrbanGardeners', bands: { min: 1000, mid: 1400, max: 2000 }, status: 'pending', createdAt: new Date().toISOString(), moderationStatus: 'active', message: 'Mid-tier includes light irrigation upgrades.', timelineEstimate: 'Crew available next month, 3-week installation.' },
		{ id: 'o3', planId: 'pl2', gardenerId: 'g3', gardenerName: 'Leaf & Stone', bands: { min: 800, mid: 1100, max: 1600 }, status: 'pending', createdAt: new Date().toISOString(), moderationStatus: 'active', message: 'Custom cottage plant mix with seasonal colour.', timelineEstimate: 'We can deliver in 4 weeks after approval.' }
	];
}

export let offers: Offer[] = loadOffers();

function saveOffers() {
	try {
		localStorage.setItem('offers', JSON.stringify(offers));
		window.dispatchEvent(new CustomEvent('offersUpdated'));
	} catch (err) {
		console.error('Failed to save offers to localStorage', err);
		throw err;
	}
}

export function getOffers(options?: { includeDisabled?: boolean }): Offer[] {
	offers = loadOffers();
	if (options?.includeDisabled) return offers;
	return offers.filter(o => o.moderationStatus !== 'disabled');
}

export function getOffersForPlan(planId: string, options?: { includeDisabled?: boolean }): Offer[] {
	return getOffers({ includeDisabled: options?.includeDisabled }).filter(o => o.planId === planId);
}

export function updateOffer(offerId: string, updates: Partial<Offer>) {
	const index = offers.findIndex(o => o.id === offerId);
	if (index === -1) return;
	const updated = normalizeOffer({ ...offers[index], ...updates });
	offers[index] = updated;
	saveOffers();
}

export function setOfferModerationStatus(offerId: string, moderationStatus: OfferModerationStatus) {
	updateOffer(offerId, { moderationStatus });
}

export function addOffer(offer: Offer) {
	const normalized = normalizeOffer(offer);
	offers.push(normalized);
	saveOffers();
}

export function acceptOffer(offerId: string) {
	const target = offers.find(o => o.id === offerId);
	if (!target) return;

	const planId = target.planId;
	offers = offers.map(o => {
		if (o.id === offerId) {
			return normalizeOffer({ ...o, status: 'accepted' });
		}
		if (o.planId === planId && o.status === 'accepted') {
			return normalizeOffer({ ...o, status: 'rejected' });
		}
		return o;
	});
	saveOffers();
}

export function rejectOffer(offerId: string) {
	const index = offers.findIndex(o => o.id === offerId);
	if (index === -1) return;
	offers[index] = normalizeOffer({ ...offers[index], status: 'rejected' });
	saveOffers();
}


